import React, { useEffect, useMemo } from 'react';
import { withTranslation } from 'react-i18next';
import { useRouteMatch } from 'react-router-dom';
import {
    useExpanded,
    useFilters,
    usePagination,
    useRowSelect,
    useSortBy,
    useTable,
} from 'react-table';
import {
    Box,
    DaTable,
    DaTableBody,
    DaTableCell,
    DaTableHeadCell,
    DaTableHead,
    DaTableRow,
    Text,
} from '@paygreen/paygreen-ui';
import {
    CarbonDataDisplay,
    PaginationTable,
    StatusDisplayTable,
    StatusFilterTable,
    HideAndCopy,
} from '../../components';
import { timestampToDate, IsBigDesktop } from '../../utils/tools';
import { statusDonations } from '../../utils/constants';
import { useUser } from '../../context';
import { statusOptions } from './statusOptions';

const DonationsTable = ({
    t,
    data,
    fetchData,
    loading,
    pageCount: controlledPageCount,
    startDate,
    endDate,
    totalItems,
    accountId,
    idAssociation,
    activePageReset,
    setActivePageReset,
    statusFilter,
    setStatusFilter,
}) => {
    const { user } = useUser();
    const isCharityDonations = !!useRouteMatch(
        '/admin/charities/:accountId/charity-donations',
    );
    const isAsso = user.role === 'ASSOCIATION' || isCharityDonations;

    // needed to guarantee vertical alignment on mobile for status cell in table
    const statusCellboxStyle = {
        display: IsBigDesktop() ? null : 'flex',
        justifyContent: IsBigDesktop() ? null : 'flex-end',
        wordBreak: 'normal',
    };

    const columns = useMemo(
        () => [
            {
                Header: t('shopDonations.table.heads.idDonation'),
                accessor: 'idDonation',
            },
            {
                Header: t('shopDonations.table.heads.createdAt'),
                id: 'createdAt',
                accessor: (d) => timestampToDate(d.createdAt),
            },
            {
                Header: t('shopDonations.table.heads.donationAmount'),
                accessor: 'donationAmount',
            },
            {
                Header: t('shopDonations.table.heads.totalAmount'),
                accessor: 'totalAmount',
            },
            {
                Header: t('shopDonations.table.heads.donationReference'),
                accessor: 'donationReference',
            },
            {
                Header: isAsso
                    ? t('charityDonations.table.heads.shop')
                    : t('shopDonations.table.heads.association'),
                accessor: isAsso ? 'shopName' : 'associationName',
            },
            {
                Header: isAsso
                    ? t('charityDonations.table.heads.idDonor')
                    : t('shopDonations.table.heads.idBuyer'),
                accessor: isAsso ? 'donorEmail' : 'buyerEmail',
            },
            {
                Header: '',
                accessor: 'status',
                Filter: ({ filter, ...rest }) => (
                    <StatusFilterTable
                        defaultValue={statusFilter}
                        onChange={setStatusFilter}
                        setActivePageReset={setActivePageReset}
                        statusConstants={statusDonations}
                        i18TextBase="shopDonations.table.status"
                        {...rest}
                    />
                ),
                filter: 'includes',
            },
        ],
        [t, isAsso, setActivePageReset, statusFilter, setStatusFilter],
    );

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page,
        pageCount,
        gotoPage,
        setPageSize,
        state: { pageIndex, pageSize },
    } = useTable(
        {
            columns,
            data,
            initialState: {
                pageSize: 25,
                hiddenColumns: [isAsso ? 'totalAmount' : null],
            },
            manualPagination: true,
            pageCount: controlledPageCount,
            manualSortBy: true,
            autoResetPage: false,
            // we ponctually override table internal state to resetPage index with component SelectColumnFilter when needed
            useControlledState: (state) => {
                if (activePageReset) {
                    return { ...state, pageIndex: 0 };
                } else {
                    return state;
                }
            },
        },
        useFilters,
        useSortBy,
        useExpanded,
        usePagination,
        useRowSelect,
    );

    useEffect(() => {
        fetchData({
            pageIndex,
            pageSize,
            startDate,
            endDate,
            statusFilter,
            accountId,
            idAssociation,
        });
    }, [
        fetchData,
        pageIndex,
        pageSize,
        startDate,
        endDate,
        statusFilter,
        accountId,
        idAssociation,
    ]);

    return (
        <Box>
            <DaTable
                {...getTableProps()}
                blockWidth="none"
                isLoading={loading}
                loadingRowNumber={15}
                loadingMainCellNumber={3}
            >
                {headerGroups.map((headerGroup) => (
                    <DaTableHead
                        data-cy="datable-donations"
                        blockTitle={t('table.title')}
                        {...headerGroup.getHeaderGroupProps()}
                    >
                        {headerGroup.headers.map((column, index) => (
                            <DaTableHeadCell
                                key={index}
                                label={
                                    column.Header ? column.render('Header') : ''
                                }
                            >
                                {column.Filter ? column.render('Filter') : null}
                            </DaTableHeadCell>
                        ))}
                    </DaTableHead>
                ))}

                <DaTableBody {...getTableBodyProps()}>
                    {!loading && data.length ? (
                        page.map((row) => {
                            prepareRow(row);

                            return (
                                <DaTableRow {...row.getRowProps()} key={row.id}>
                                    <DaTableCell
                                        isMain={false}
                                        label={t(
                                            'shopDonations.table.heads.idDonation',
                                        )}
                                    >
                                        {row.values.idDonation}
                                    </DaTableCell>

                                    <DaTableCell
                                        label={t(
                                            'shopDonations.table.heads.createdAt',
                                        )}
                                    >
                                        {row.values.createdAt}
                                    </DaTableCell>

                                    <DaTableCell
                                        label={t(
                                            'shopDonations.table.heads.donationAmount',
                                        )}
                                    >
                                        <CarbonDataDisplay
                                            value={row.values.donationAmount}
                                            mode="price"
                                            hasZeroDisplayed={true}
                                        />
                                    </DaTableCell>

                                    <DaTableCell
                                        isMain={false}
                                        label={t(
                                            'shopDonations.table.heads.totalAmount',
                                        )}
                                        style={{
                                            display: isAsso ? 'none' : null,
                                        }}
                                    >
                                        <CarbonDataDisplay
                                            value={row.values.totalAmount}
                                            mode="price"
                                            hasZeroDisplayed={true}
                                        />
                                    </DaTableCell>

                                    <DaTableCell
                                        isMain={false}
                                        label={t(
                                            'shopDonations.table.heads.donationReference',
                                        )}
                                        title={
                                            row.values.donationReference || ''
                                        }
                                    >
                                        {row.values.donationReference ? (
                                            <HideAndCopy
                                                text={
                                                    row.values.donationReference
                                                }
                                                startLength={5}
                                                endLength={5}
                                            />
                                        ) : (
                                            t(
                                                'charityDonations.table.rows.notAvailable',
                                            )
                                        )}
                                    </DaTableCell>

                                    <DaTableCell
                                        isMain={false}
                                        label={
                                            isAsso
                                                ? t(
                                                      'charityDonations.table.heads.shop',
                                                  )
                                                : t(
                                                      'shopDonations.table.heads.association',
                                                  )
                                        }
                                    >
                                        {isAsso
                                            ? row.values.shopName
                                            : row.values.associationName}
                                    </DaTableCell>

                                    <DaTableCell
                                        isMain={false}
                                        label={
                                            isAsso
                                                ? t(
                                                      'charityDonations.table.heads.idDonor',
                                                  )
                                                : t(
                                                      'shopDonations.table.heads.idBuyer',
                                                  )
                                        }
                                    >
                                        {isAsso
                                            ? row.values.donorEmail ||
                                              t(
                                                  'charityDonations.table.rows.notAvailable',
                                              )
                                            : row.values.buyerEmail}
                                    </DaTableCell>

                                    <DaTableCell
                                        style={{ ...statusCellboxStyle }}
                                    >
                                        <StatusDisplayTable
                                            status={row.values.status}
                                            statusOptions={statusOptions}
                                            i18TextBase="shopDonations.table.status"
                                        />
                                    </DaTableCell>
                                </DaTableRow>
                            );
                        })
                    ) : (
                        <Text
                            align="center"
                            marginTop="xl"
                            marginBottom="xl"
                            textSize="sm"
                        >
                            {t('noDataFound')}
                        </Text>
                    )}
                </DaTableBody>
            </DaTable>

            {totalItems > 25 ? (
                <PaginationTable
                    setPageSize={setPageSize}
                    pageCount={pageCount || 1}
                    pageIndex={pageIndex}
                    colorTheme="quaternary"
                    gotoPage={gotoPage}
                    setActivePageReset={setActivePageReset}
                />
            ) : null}
        </Box>
    );
};

export default withTranslation('common')(DonationsTable);
